import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Button, Link, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  SignatureType,
  UploadPath,
  useDocument_UploadFileMutation,
  useDocumentTag_GetAllDocumentTagsQuery,
} from 'queries';

import Modal from 'common/modal';
import { UPLOAD_LIMIT_BYTES, UPLOAD_LIMIT_STRING } from 'utils/constants';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasSuperAdmin from 'utils/useHasSuperAdmin';
import useIsImpersonating from 'utils/useIsImpersonating';

import { BulkUploadPreview } from './bulk-upload-preview/bulk-upload-preview';
import { signatureTypeSelectOptions, uploadPathSelectOptions } from './bulk-upload.constants';
import { ScrollArea, useStyles } from './bulk-upload.styles';
import { BulkUploadAutocompleteSelect } from './bulk-upload-autocomplete-select';
import { bulkUploadFileValidator, useFileDropzone } from './use-file-dropzone.hook';

type BulkUploadModalProps = {
  setShowModal: (showModal: boolean) => void;
  onClose: () => void;
  showModal: boolean;
  portalId: string;
  parentId: string;
};

export const BulkUploadModal = ({
  setShowModal,
  onClose,
  showModal,
  portalId,
  parentId,
}: BulkUploadModalProps) => {
  const classes = useStyles();
  const history = useHistory();
  const isSuperAdmin = useHasSuperAdmin();
  const isImpersonating = useIsImpersonating();
  const isAdmin = isSuperAdmin || isImpersonating;

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [documentTags, setDocumentTags] = useState<Record<string, string>>({});
  const [uploadPaths, setUploadPaths] = useState<Record<string, UploadPath | undefined>>({});
  const [signatureTypes, setSignatureTypes] = useState<Record<string, SignatureType>>({});

  const [uploadFile] = useDocument_UploadFileMutation({
    refetchQueries: [],
    onError: (error) => {
      showAlert({
        message: 'Error uploading files: ' + error.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: documentTagsData, loading: documentTagsDataLoading } =
    useDocumentTag_GetAllDocumentTagsQuery({
      onError: (error) => {
        showAlert({
          message: 'Error getting all document tags: ' + error.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const documentTagsList = (documentTagsData?.data || []).map((tag) => ({
    label: tag.name,
    value: tag.id,
  }));
  const defaultDocumentTag = documentTagsData?.data.find((tag) => tag.name === 'Other');

  const dropzone = useFileDropzone({
    onValidationError: (errors) => {
      if (errors.duplicateNames.length > 0) {
        const invalidFiles = errors.duplicateNames.join(', ');
        showAlert({
          message: `File(s) with the same name already exist: ${invalidFiles}. Please remove or rename file(s) and try again.`,
          severity: NotificationSeverity.ERROR,
        });
      }
      if (errors.oversizedFiles.length > 0) {
        const oversizedFiles = errors.oversizedFiles.join(', ');
        showAlert({
          message: `File(s) too large: ${oversizedFiles}. Maximum file size is ${UPLOAD_LIMIT_STRING}.`,
          severity: NotificationSeverity.ERROR,
        });
      }
    },
    validator: bulkUploadFileValidator({
      maxFileSize: UPLOAD_LIMIT_BYTES,
      preventDuplicateNames: true,
    }),
    onFilesAdded: (files) => {
      setDocumentTags((prev) => ({
        ...prev,
        ...Object.fromEntries(files.map((file) => [file.name, defaultDocumentTag?.id ?? ''])),
      }));
      setUploadPaths((prev) => ({
        ...prev,
        ...Object.fromEntries(files.map((file) => [file.name, undefined])),
      }));
      setSignatureTypes((prev) => ({
        ...prev,
        ...Object.fromEntries(files.map((file) => [file.name, SignatureType.NoSignature])),
      }));
    },
  });

  const prepareDocumentsForUpload = () => {
    return dropzone.files.map((file) => {
      const uploadPath = uploadPaths[file.name];
      const documentTagId = documentTags[file.name];
      const signatureType = signatureTypes[file.name];
      if (!documentTagId || !signatureType) {
        showAlert({
          message: 'Error preparing documents for upload: Missing required data',
          severity: NotificationSeverity.ERROR,
        });
        return Promise.resolve();
      }

      return uploadFile({
        variables: {
          input: {
            file,
            uploadPath,
            documentTagId,
            portalId,
            parentId,
            signatureType,
            needsSignature: false,
          },
        },
        context: {
          fetchOptions: {
            onProgress: (ev: ProgressEvent) => {
              setUploadProgress((prevProgress) => ({
                ...prevProgress,
                [file.name]: (ev.loaded / ev.total) * 100,
              }));
            },
          },
        },
      });
    });
  };

  const handleStartUpload = async () => {
    setIsUploading(true);
    await Promise.all(prepareDocumentsForUpload());
    setIsUploading(false);
    onClose();
    history.go(0);
  };

  const handleExitClick = () => {
    if (
      isUploading &&
      !window.confirm('Uploading is in progress. Are you sure you want to exit?')
    ) {
      return;
    }
    setShowModal(false);
    onClose();
  };

  const onBulkAssignUploadPath = (selected?: UploadPath) => {
    setUploadPaths((prev) => ({
      ...prev,
      ...Object.fromEntries(dropzone.files.map((file) => [file.name, selected])),
    }));
  };
  const onBulkAssignSignatureType = (selected?: SignatureType) => {
    setSignatureTypes((prev) => ({
      ...prev,
      ...Object.fromEntries(
        dropzone.files.map((file) => [file.name, selected || SignatureType.NoSignature]),
      ),
    }));
  };
  const onBulkAssignDocumentTag = (selected?: string) => {
    if (selected) {
      setDocumentTags((prev) => ({
        ...prev,
        ...Object.fromEntries(dropzone.files.map((file) => [file.name, selected])),
      }));
    }
  };

  return (
    <Modal
      setShowModal={handleExitClick}
      onExitClick={handleExitClick}
      showModal={showModal}
      showActions={false}
      loading={documentTagsDataLoading}
      title={<Typography className={classes.title}>Bulk upload</Typography>}
      dialogActions={
        dropzone.files.length > 0 && (
          <Button
            variant="contained"
            className={classes.uploadButton}
            onClick={handleStartUpload}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Start Upload'}
          </Button>
        )
      }
      content={
        <Box className={classes.content}>
          <Box className={classes.stickyContainer}>
            <Box
              className={clsx(
                classes.uploadContainer,
                dropzone.dragActive && classes.mutedBackground,
                dropzone.files.length > 0 && classes.reducedHeight,
              )}
              onDragEnter={dropzone.handleDrag}
              onDragLeave={dropzone.handleDrag}
              onDragOver={dropzone.handleDrag}
              onDrop={dropzone.handleDrop}
              onClick={dropzone.openFileDialog}
            >
              <Box className={classes.uploadContent}>
                <Box className={classes.uploadContentIcon}>
                  <FileUploadOutlinedIcon className={classes.uploadIcon} />
                </Box>
                <Typography>
                  <Link className={classes.clickHereLink} underline="none">
                    Click here{' '}
                  </Link>
                  to browse files or drag and drop files here.
                </Typography>
                <Typography className={classes.uploadContentSubtitle}>
                  Max single file size: {UPLOAD_LIMIT_STRING}
                </Typography>
                <input
                  ref={dropzone.inputRef}
                  type="file"
                  onChange={dropzone.handleChange}
                  multiple
                  className={classes.hidden}
                  id="fileInput"
                />
              </Box>
            </Box>
            {dropzone.files.length > 0 && (
              <Typography className={classes.uploadPreviewText}>
                Upload Preview ({dropzone.files.length})
              </Typography>
            )}
          </Box>
          {dropzone.files.length > 0 && (
            <Box className={classes.fileUploadPreviewContainer}>
              <ScrollArea>
                <Box className={classes.bulkAssignContainer}>
                  <Typography className={classes.bulkAssignTitle}>Bulk assign</Typography>
                  <Box className={classes.tagsInputs}>
                    {isAdmin && (
                      <>
                        <BulkUploadAutocompleteSelect
                          className={classes.tagInputField}
                          options={uploadPathSelectOptions}
                          label="Bulk assign upload path"
                          value={null}
                          onChange={(selected) => onBulkAssignUploadPath(selected?.value)}
                        />
                        <BulkUploadAutocompleteSelect
                          className={classes.tagInputField}
                          options={signatureTypeSelectOptions}
                          label="Bulk assign signature type"
                          value={null}
                          onChange={(selected) => onBulkAssignSignatureType(selected?.value)}
                        />
                      </>
                    )}
                    <BulkUploadAutocompleteSelect
                      className={classes.tagInputField}
                      options={documentTagsList}
                      value={null}
                      onChange={(selected) => onBulkAssignDocumentTag(selected?.value)}
                      label="Bulk assign document tag"
                    />
                  </Box>
                </Box>
                <Box className={classes.fileUploadPreview}>
                  {dropzone.files.map((file) => (
                    <BulkUploadPreview
                      key={file.name}
                      file={file}
                      isAdmin={isAdmin}
                      uploadProgress={uploadProgress}
                      removeFile={dropzone.handleRemoveFile}
                      isUploading={isUploading}
                      documentTagsList={documentTagsList}
                      uploadPath={uploadPaths?.[file.name]}
                      onUploadPathChange={(selected) =>
                        setUploadPaths((prev) => ({ ...prev, [file.name]: selected }))
                      }
                      signatureType={signatureTypes?.[file.name] ?? SignatureType.NoSignature}
                      onSignatureTypeChange={(selected) =>
                        setSignatureTypes((prev) => ({ ...prev, [file.name]: selected }))
                      }
                      documentTag={documentTags?.[file.name] ?? defaultDocumentTag?.id ?? ''}
                      onDocumentTagChange={(selected) =>
                        setDocumentTags((prev) => ({
                          ...prev,
                          [file.name]: selected ?? defaultDocumentTag?.id ?? '',
                        }))
                      }
                    />
                  ))}
                </Box>
              </ScrollArea>
            </Box>
          )}
        </Box>
      }
      actionDisabled={false}
      maxWidth="md"
    />
  );
};
